import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Modal,
  Progress,
  Result,
  Space,
  Steps,
  Switch,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import {
  InboxOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useResponsive } from '../../hooks/useResponsive';
import {
  useAddMenuIngestPageMutation,
  useConfirmMenuIngestMutation,
  useDiscardMenuIngestMutation,
  useGetPendingMenuIngestsQuery,
  useSaveMenuIngestDraftMutation,
  useStartMenuIngestMutation,
} from '../../services/menuIngestApi';
import type { MenuDraftItem, MenuIngest, MenuIngestConfirmResult } from '../../types/menuIngest';
import { MENU_DRAFT_FLAGS } from '../../types/menuIngest';
import { MAX_PAGES, normalizeMenuFiles } from '../../utils/menuPageNormalizer';
import { newClientId } from '../../utils/clientId';
import MenuDraftTable from './MenuDraftTable';
import MenuIngestPageImage from './MenuIngestPageImage';

const { Text, Title, Paragraph } = Typography;
const { Dragger } = Upload;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Se dispara cuando se crearon productos, para que la pantalla de menú se refresque. */
  onImported?: () => void;
}

// Id para un renglón nuevo. Tiene que ser un UUID de verdad: viaja al servidor dentro
// del borrador y allá la propiedad es un `Guid`; otra cosa hace fallar el PUT completo
// y el usuario perdería todas sus correcciones por haber agregado un renglón.
const newLocalId = newClientId;

const blocksCreation = (item: MenuDraftItem) =>
  item.excluded ||
  item.flags.includes(MENU_DRAFT_FLAGS.yaExiste) ||
  item.flags.includes(MENU_DRAFT_FLAGS.duplicado);

const needsAttention = (item: MenuDraftItem) =>
  item.price === null ||
  item.flags.length > 0 ||
  (item.source === 'Ia' && (item.priceConfidence === 'Low' || item.nameConfidence === 'Low'));

/**
 * Cargar la carta de papel: subir → revisar → crear.
 *
 * **Componente propio y no un pedazo de MenuManagement** a propósito: esa
 * pantalla la está reescribiendo otro PR, y mantener la ingesta aquí deja el
 * conflicto reducido al botón que la abre. Mismo precedente que
 * `CsvImportModal`.
 *
 * El paso de revisión no es opcional ni saltable. Un precio mal leído
 * multiplicado por ochenta productos es un desastre de soporte que además nadie
 * detecta hasta que un cliente paga de menos: por eso no existe ningún camino de
 * "confirmar todo sin mirar".
 */
const MenuIngestModal: React.FC<Props> = ({ open, onClose, onImported }) => {
  // AntD v5: el `message` estático no tiene contexto y sus avisos no se ven.
  const { message, modal } = App.useApp();
  const { isMobile } = useResponsive();

  const [step, setStep] = useState(0);
  const [ingestion, setIngestion] = useState<MenuIngest | null>(null);
  const [items, setItems] = useState<MenuDraftItem[]>([]);
  const [dirty, setDirty] = useState(false);
  const [focusedItemId, setFocusedItemId] = useState<string | null>(null);
  const [onlyPending, setOnlyPending] = useState(false);
  /** Los ids que necesitaban revisión cuando se prendió el filtro. Ver `visibleItems`. */
  const [pendingSnapshot, setPendingSnapshot] = useState<Set<string> | null>(null);
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null);
  const [confirmResult, setConfirmResult] = useState<MenuIngestConfirmResult | null>(null);

  const { data: pending = [], refetch: refetchPending } = useGetPendingMenuIngestsQuery(undefined, {
    skip: !open,
  });

  const [startIngest] = useStartMenuIngestMutation();
  const [addPage] = useAddMenuIngestPageMutation();
  const [saveDraft, { isLoading: saving }] = useSaveMenuIngestDraftMutation();
  const [confirmIngest, { isLoading: confirming }] = useConfirmMenuIngestMutation();
  const [discardIngest] = useDiscardMenuIngestMutation();

  const reset = useCallback(() => {
    setStep(0);
    setIngestion(null);
    setItems([]);
    setDirty(false);
    setFocusedItemId(null);
    setOnlyPending(false);
    setPendingSnapshot(null);
    setUploading(null);
    setConfirmResult(null);
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const adopt = useCallback((next: MenuIngest) => {
    setIngestion(next);
    setItems(next.items);
    setDirty(false);
  }, []);

  /**
   * Manda el borrador al servidor y adopta lo que devuelve, ya revalidado.
   *
   * Se declara antes que la subida porque ésta lo usa: subir una página trae de
   * vuelta el borrador persistido completo, así que lo que no se haya guardado
   * antes se pierde.
   */
  const persistDraft = useCallback(async () => {
    if (!ingestion) return null;
    // El servidor descarta por DOS razones distintas y hay que distinguirlas: los renglones
    // sin nombre se caen siempre (no son productos ni pueden llegar a serlo) y los que no
    // caben se caen sólo al topar. Contar todo junto hacía que agregar un renglón vacío a una
    // carga de doce productos avisara "llegó al tope", que es sencillamente falso.
    const blank = items.filter((i) => !i.name.trim()).length;
    const named = items.length - blank;

    const updated = await saveDraft({ ingestionId: ingestion.id, items }).unwrap();

    if (blank > 0) {
      message.info(
        `${blank} renglón${blank === 1 ? '' : 'es'} sin nombre no se guardó. ` +
          'Escríbele el nombre si lo quieres conservar.'
      );
    }

    // Sin avisar, un renglón que acabas de agregar a mano simplemente no está después de
    // guardar — sin error y sin nada que mirar, que es el peor desenlace de esta pantalla.
    const lost = named - updated.items.length;
    if (lost > 0) {
      message.warning(
        `${lost} renglón${lost === 1 ? '' : 'es'} no cupo en esta carga: llegó al tope de ` +
          'productos. Confírmala y empieza otra para el resto.'
      );
    }

    adopt(updated);
    return updated;
  }, [ingestion, items, saveDraft, adopt, message]);

  // ── Subida ────────────────────────────────────────────────────────────────

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      setUploading({ done: 0, total: files.length });

      try {
        // Lo que el usuario haya corregido se guarda ANTES de subir nada.
        //
        // Cada página que vuelve del servidor trae el borrador persistido completo
        // y reemplaza lo que hay en pantalla —es lo que permite que una página 3
        // detecte un duplicado de la página 1—, así que sin este guardado las
        // correcciones sin guardar se perderían sin aviso. Guardar es mejor que
        // preguntar: no hay respuesta razonable a "¿descarto tus correcciones?".
        if (dirty && ingestion) {
          try {
            await persistDraft();
          } catch {
            message.error('No se pudieron guardar tus correcciones. No se subió nada.');
            return;
          }
        }

        // El presupuesto es lo que QUEDA, no el tope entero: con seis páginas ya
        // subidas, normalizar diez más sólo sirve para rasterizarlas, subirlas y
        // que el servidor las rechace de una en una.
        const remaining = MAX_PAGES - (ingestion?.pages.length ?? 0);
        if (remaining <= 0) {
          message.warning(
            `Esta carga ya tiene ${MAX_PAGES} páginas. Confírmala y empieza otra para subir más.`
          );
          return;
        }

        // Normalizar: aquí es donde el HEIC del iPhone se vuelve JPEG y donde un
        // PDF se abre en una imagen por página. Hasta no saber cuántas páginas
        // salieron, no se puede decir "página 2 de N".
        const { pages, warnings } = await normalizeMenuFiles(files, remaining);
        warnings.forEach((w) => message.warning(w));

        if (pages.length === 0) {
          message.error('No se pudo preparar ninguna página para leer.');
          return;
        }

        let current = ingestion;
        if (!current) {
          try {
            current = await startIngest().unwrap();
            setIngestion(current);
          } catch (err: any) {
            // 403 si el módulo está apagado —nace apagado— y 507 si ya hay
            // demasiadas cargas sin confirmar. Sin este catch, el spinner
            // parpadea y no pasa nada más: el usuario no sabe si falló o si su
            // carta simplemente no tenía productos.
            message.error(
              err?.data?.message ||
                'No se pudo iniciar la carga. Revisa que la función esté activada para tu restaurante.'
            );
            return;
          }
        }

        setUploading({ done: 0, total: pages.length });

        // Secuencial y no en paralelo: cada página es una llamada al modelo, el
        // número de página se calcula contando las que ya hay, y así el progreso
        // que se muestra es el real y no una barra que salta.
        for (let i = 0; i < pages.length; i++) {
          const page = pages[i];
          try {
            const updated = await addPage({
              ingestionId: current.id,
              file: page.blob,
              fileName: page.fileName,
              totalPages: pages.length,
            }).unwrap();

            adopt(updated);
          } catch (err: any) {
            // Una página que el servidor rechaza no cancela las demás: si de
            // cuatro fotos una salió movida, las otras tres ya se pagaron.
            message.error(err?.data?.message || `No se pudo subir "${page.fileName}"`);
          } finally {
            setUploading({ done: i + 1, total: pages.length });
          }
        }

        setStep(1);
      } finally {
        setUploading(null);
      }
    },
    [ingestion, dirty, persistDraft, startIngest, addPage, adopt, message]
  );

  // ── Edición ───────────────────────────────────────────────────────────────

  const patchItem = useCallback((id: string, patch: Partial<MenuDraftItem>) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        // Marcar el renglón como editado en cuanto cambia un campo de contenido:
        // es lo que distingue después "esto lo leyó la IA" de "esto lo escribí
        // yo". Excluir no cuenta — no cambia el dato, sólo si se crea.
        const touchedContent =
          'name' in patch || 'price' in patch || 'categoryName' in patch || 'description' in patch;

        return {
          ...item,
          ...patch,
          source: touchedContent && item.source === 'Ia' ? 'Editado' : item.source,
        };
      })
    );
    setDirty(true);
  }, []);

  const addBlankItem = useCallback(() => {
    const blank: MenuDraftItem = {
      // Id temporal del cliente; el servidor le pone uno propio al guardar. Lo
      // único que importa aquí es que sea único dentro de la tabla, porque los
      // renglones se identifican por Id y no por posición.
      id: newLocalId(),
      name: '',
      description: null,
      price: null,
      categoryName: items[items.length - 1]?.categoryName ?? '',
      nameConfidence: 'High',
      priceConfidence: 'High',
      source: 'Agregado',
      pageNumber: 0,
      flags: [],
      excluded: false,
    };
    setItems((prev) => [...prev, blank]);
    setDirty(true);
  }, [items]);

  const handleSave = useCallback(async () => {
    try {
      await persistDraft();
      message.success('Borrador guardado');
    } catch {
      message.error('No se pudo guardar el borrador');
    }
  }, [persistDraft, message]);

  // ── Confirmación ──────────────────────────────────────────────────────────

  const creatable = useMemo(() => items.filter((i) => !blocksCreation(i)), [items]);
  const missingPrice = useMemo(() => creatable.filter((i) => i.price === null), [creatable]);

  const handleConfirm = useCallback(async () => {
    if (!ingestion) return;

    const run = async () => {
      try {
        // Se guarda antes de confirmar: el servidor crea desde el borrador
        // persistido, no desde lo que tenga la pantalla. Sin esto, la última
        // corrección se perdería justo al confirmar.
        await persistDraft();

        const result = await confirmIngest(ingestion.id).unwrap();
        setConfirmResult(result);
        setStep(2);

        if (result.successCount > 0) onImported?.();
      } catch (err: any) {
        message.error(err?.data?.message || 'No se pudieron crear los productos');
      }
    };

    if (missingPrice.length > 0) {
      modal.confirm({
        title: `${missingPrice.length} producto${missingPrice.length === 1 ? '' : 's'} sin precio`,
        content:
          'Los productos sin precio no se van a crear. Puedes escribirles el precio ahora o ' +
          'continuar sin ellos.',
        okText: 'Continuar sin ellos',
        cancelText: 'Volver a revisar',
        onOk: run,
      });
      return;
    }

    await run();
  }, [ingestion, persistDraft, confirmIngest, missingPrice, modal, message, onImported]);

  const handleClose = useCallback(() => {
    if (dirty && step === 1) {
      modal.confirm({
        title: 'Tienes cambios sin guardar',
        content:
          'Tu carga queda guardada y la puedes retomar después, pero las correcciones que no ' +
          'guardaste se pierden.',
        okText: 'Salir sin guardar',
        cancelText: 'Seguir revisando',
        onOk: onClose,
      });
      return;
    }
    onClose();
  }, [dirty, step, modal, onClose]);

  const handleResume = useCallback(
    (candidate: MenuIngest) => {
      adopt(candidate);
      setStep(1);
    },
    [adopt]
  );

  const handleDiscard = useCallback(
    async (id: string) => {
      try {
        await discardIngest(id).unwrap();
        refetchPending();
        if (ingestion?.id === id) reset();
        message.success('Carga descartada');
      } catch {
        message.error('No se pudo descartar la carga');
      }
    },
    [discardIngest, refetchPending, ingestion, reset, message]
  );

  // ── Render ────────────────────────────────────────────────────────────────

  // El filtro es PEGAJOSO, no congelado: se ve lo que necesitaba revisión cuando se prendió
  // MÁS lo que la necesita ahora.
  //
  // Las dos mitades resuelven un problema distinto y hacen falta las dos:
  //
  //  - La foto (`pendingSnapshot`) evita que la fila se desmonte mientras la corriges. Con un
  //    filtro puramente vivo, editar un renglón dudoso lo saca de la lista a la PRIMERA letra
  //    —deja de "necesitar revisión"— y se pierde el foco con el cursor dentro; le pasaba
  //    justo a los renglones que el filtro existe para sacar a la luz.
  //  - El `needsAttention` de ahora evita lo contrario: con la foto sola, un renglón agregado
  //    después —a mano, o traído por otra página— sería invisible, y "Agregar producto"
  //    parecería no hacer nada.
  const visibleItems =
    onlyPending && pendingSnapshot
      ? items.filter((i) => pendingSnapshot.has(i.id) || needsAttention(i))
      : items;
  const focusedItem = items.find((i) => i.id === focusedItemId) ?? null;
  const focusedPage =
    ingestion?.pages.find((p) => p.pageNumber === focusedItem?.pageNumber) ?? null;

  const renderUpload = () => (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="Súbele una foto a tu carta y te propongo los productos"
        description={
          <>
            <Paragraph style={{ marginBottom: 4 }}>
              Toma una foto por página, o sube el PDF de tu carta. Puedes cargar hasta{' '}
              {MAX_PAGES} páginas de una vez.
            </Paragraph>
            <Text type="secondary">
              Nada se crea todavía: cuando termine de leer vas a poder revisar y corregir cada
              producto antes de darlos de alta. Las imágenes se envían a un servicio externo de
              IA para leerlas.
            </Text>
          </>
        }
      />

      {pending.length > 0 && !ingestion && (
        <Alert
          type="warning"
          showIcon
          message="Tienes una carga sin terminar"
          description={
            <Space direction="vertical" style={{ width: '100%' }}>
              {pending.map((candidate) => (
                <Space key={candidate.id} wrap>
                  <Text>
                    {candidate.pages.length} página{candidate.pages.length === 1 ? '' : 's'} ·{' '}
                    {candidate.items.length} producto{candidate.items.length === 1 ? '' : 's'}
                  </Text>
                  <Button size="small" type="primary" onClick={() => handleResume(candidate)}>
                    Continuar
                  </Button>
                  <Button size="small" danger onClick={() => handleDiscard(candidate.id)}>
                    Descartar
                  </Button>
                </Space>
              ))}
            </Space>
          }
        />
      )}

      {uploading ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Progress
            percent={Math.round((uploading.done / uploading.total) * 100)}
            status="active"
          />
          <Text>
            Leyendo página {Math.min(uploading.done + 1, uploading.total)} de {uploading.total}…
          </Text>
          <div>
            <Text type="secondary">Puede tardar hasta un minuto por página.</Text>
          </div>
        </div>
      ) : (
        <Dragger
          accept="image/*,.pdf,.heic,.heif"
          multiple
          showUploadList={false}
          // fileList vacío y controlado: sin esto, AntD acumula los archivos de
          // cada selección y una segunda tanda arrastraría también la primera.
          fileList={[]}
          beforeUpload={(file, batch) => {
            // beforeUpload corre UNA VEZ POR ARCHIVO, y el segundo argumento trae
            // la tanda completa. Actuar en cada llamada subiría el lote entero
            // tantas veces como archivos tenga; se espera al último.
            if (file === batch[batch.length - 1]) {
              handleFiles(batch as File[]);
            }
            return false;
          }}
          style={{ padding: '20px 0' }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Toca para tomar una foto o elegir archivos</p>
          <p className="ant-upload-hint">Fotos (JPG, PNG, HEIC) o un PDF de tu carta</p>
        </Dragger>
      )}
    </Space>
  );

  const renderReview = () => (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <Tag color="success">{creatable.length} por crear</Tag>
        {missingPrice.length > 0 && <Tag color="error">{missingPrice.length} sin precio</Tag>}
        {items.length - creatable.length > 0 && (
          <Tag>{items.length - creatable.length} no se crean</Tag>
        )}
        <Text type="secondary">{items.length} renglones leídos</Text>
        <Switch
          size="small"
          checked={onlyPending}
          onChange={(checked) => {
            // La foto se toma AL PRENDER y se tira al apagar. Volver a prenderlo recalcula,
            // que es como se "recoge" lo que ya quedó resuelto.
            setPendingSnapshot(
              checked ? new Set(items.filter(needsAttention).map((i) => i.id)) : null
            );
            setOnlyPending(checked);
          }}
          checkedChildren="Sólo lo que hay que revisar"
          unCheckedChildren="Ver todo"
        />
      </Space>

      <Alert
        type="info"
        showIcon
        message="Revisa antes de crear"
        description="Lo subrayado con puntitos es lo que la IA leyó con dudas. Todo es editable: toca cualquier celda para corregirla."
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 320px',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <MenuDraftTable
          items={visibleItems}
          onChange={patchItem}
          onFocusPage={(item) => setFocusedItemId(item.id)}
          focusedId={focusedItemId}
          compact={isMobile}
        />

        {/* Panel con fondo y borde: es un modal grande y sin contención las dos
            mitades se leen como una sola cosa revuelta. */}
        {!isMobile && ingestion && (
          <div
            style={{
              position: 'sticky',
              top: 0,
              padding: 12,
              borderRadius: 8,
              background: 'var(--ant-color-fill-quaternary)',
            }}
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              {focusedPage
                ? `Página ${focusedPage.pageNumber} de tu carta`
                : 'Tu carta'}
            </Text>
            <MenuIngestPageImage
              ingestionId={ingestion.id}
              pageId={focusedPage?.id ?? null}
              alt={`Página ${focusedPage?.pageNumber ?? ''} de la carta`}
            />
          </div>
        )}
      </div>

      {ingestion?.pages.some((p) => p.note) && (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          {ingestion.pages
            .filter((p) => p.note)
            .map((p) => (
              <Text key={p.id} type="secondary">
                Página {p.pageNumber}: {p.note}
              </Text>
            ))}
        </Space>
      )}
    </Space>
  );

  const renderResult = () => (
    <div style={{ padding: '8px 0' }}>
      <Result
        status={confirmResult && confirmResult.successCount > 0 ? 'success' : 'error'}
        title={
          confirmResult && confirmResult.successCount > 0
            ? `${confirmResult.successCount} productos creados`
            : 'No se creó ningún producto'
        }
        subTitle={
          confirmResult ? (
            <Space direction="vertical">
              {confirmResult.categoriesCreated > 0 && (
                <Text>
                  {confirmResult.categoriesCreated} categoría
                  {confirmResult.categoriesCreated === 1 ? '' : 's'} nueva
                  {confirmResult.categoriesCreated === 1 ? '' : 's'}
                </Text>
              )}
              {confirmResult.errorCount > 0 && (
                <Text type="danger">
                  {confirmResult.errorCount} renglón{confirmResult.errorCount === 1 ? '' : 'es'} sin
                  crear
                </Text>
              )}
            </Space>
          ) : null
        }
      />

      {confirmResult && confirmResult.errors.length > 0 && (
        <>
          <Title level={5}>Lo que no se pudo crear</Title>
          <Table
            dataSource={confirmResult.errors}
            columns={[
              { title: 'Renglón', dataIndex: 'rowNumber', key: 'rowNumber', width: 80 },
              {
                title: 'Producto',
                key: 'rowData',
                render: (_: unknown, row: { rowData: Record<string, string> }) =>
                  Object.values(row.rowData || {}).filter(Boolean).join(' · ') || '—',
                ellipsis: true,
              },
              {
                title: 'Motivo',
                dataIndex: 'errors',
                key: 'errors',
                render: (errors: string[]) => errors.join('. '),
              },
            ]}
            // Compuesta con el índice: varios renglones sin precio comparten el mismo
            // número, y con la llave repetida React pinta la celda equivocada al paginar.
            rowKey={(row, index) => `${row.rowNumber}-${index}`}
            size="small"
            pagination={confirmResult.errors.length > 5 ? { pageSize: 5, size: 'small' } : false}
            scroll={{ x: 'max-content' }}
          />
        </>
      )}
    </div>
  );

  const footer = () => {
    if (step === 1) {
      return [
        <Button key="more" icon={<ReloadOutlined />} onClick={() => setStep(0)}>
          Subir más páginas
        </Button>,
        <Button key="add" icon={<PlusOutlined />} onClick={addBlankItem}>
          Agregar producto
        </Button>,
        <Button key="save" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          Guardar borrador
        </Button>,
        <Button
          key="confirm"
          type="primary"
          icon={<CheckOutlined />}
          loading={confirming}
          disabled={creatable.length === 0}
          onClick={handleConfirm}
        >
          Crear {creatable.length} producto{creatable.length === 1 ? '' : 's'}
        </Button>,
      ];
    }

    if (step === 2) {
      return [
        <Button key="close" type="primary" onClick={onClose}>
          Cerrar
        </Button>,
      ];
    }

    return null;
  };

  return (
    <Modal
      open={open}
      title="Cargar mi carta con IA"
      onCancel={handleClose}
      width={isMobile ? '100%' : 1100}
      footer={footer()}
      maskClosable={false}
      destroyOnClose
    >
      <Steps
        current={step}
        size="small"
        style={{ marginBottom: 20 }}
        items={[{ title: 'Subir' }, { title: 'Revisar' }, { title: 'Listo' }]}
      />

      {step === 0 && renderUpload()}
      {step === 1 && renderReview()}
      {step === 2 && renderResult()}
    </Modal>
  );
};

export default MenuIngestModal;
